/**
 * unlockAllStaleLocks_v2.js
 *
 * FT-482: Self-healing one-time IDB migration for stale operation_data locks.
 *
 * PROBLEM:
 *   startOperationV2() creates an operation_data log entry with locked:true.
 *   unlockStaleOpLocks_v1 (FT-481) fixed the case where a matching operation_complete
 *   entry exists. However, edge cases remain where operation_data entries can be
 *   stuck locked:true with no corresponding operation_complete — for example, if a
 *   voyage was abandoned mid-operation or the app crashed before completing.
 *
 *   NOTE: locked:true on an operation_data entry is a DISPLAY flag only — it renders
 *   a "(Data locked — operation in progress)" caption in LogEntry.js. It does not gate
 *   voyage completion (that is controlled by section/operation_complete entry pairs in
 *   findActiveSection). This migration removes stale display artefacts.
 *
 * FIX:
 *   On startup, scan ALL operations_voyages records in IDB. For any voyage with an
 *   operation_data entry where locked===true, check the entry's creation age by
 *   parsing the millisecond timestamp from its ID field (format: entry-{ms}-{random}).
 *   If the entry is older than 5 minutes, set locked:false unconditionally — any
 *   operation still genuinely in-progress after 5 minutes would have already surfaced
 *   a problem. Write the corrected record back to IDB silently.
 *
 * GUARD:
 *   localStorage flag sr.migration.unlockAllStaleLocks_v2 = 'complete' prevents
 *   re-runs. Written at the END, only on success. On error: flag is NOT set so the
 *   migration retries on next boot.
 *
 * DIFFERENCE FROM v1:
 *   v1 required a matching operation_complete entry (locked op that was completed).
 *   v2 is unconditional age-based: any locked:true entry older than 5 minutes is
 *   unlocked regardless of whether operation_complete exists. This catches abandoned
 *   or orphaned locks that v1 misses.
 *
 * PATTERN:
 *   Follows unlockStaleOpLocks (FT-481) pattern: localStorage flag guard, try/catch,
 *   reads/writes IDB (Dexie) storage table, console log on outcomes.
 *
 * CALLER:
 *   src/index.js — fire-and-forget async call AFTER root.render() so React is
 *   already mounted before the write completes.
 */

import { db } from '../../lib/db';

const MIGRATION_FLAG = 'sr.migration.unlockAllStaleLocks_v2';
const STALE_THRESHOLD_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Parse the creation timestamp from an entry ID.
 * Entry IDs have the format: entry-{milliseconds}-{randomStr}
 * e.g. entry-1788956791014-foi8u4p
 *
 * @param {string} id
 * @returns {number|null} - Parsed millisecond timestamp, or null if unparseable.
 */
function parseEntryTimestamp(id) {
  if (!id || typeof id !== 'string') return null;
  const match = id.match(/^entry-(\d+)-/);
  if (!match) return null;
  const ms = parseInt(match[1], 10);
  if (isNaN(ms)) return null;
  return ms;
}

/**
 * Run the age-based stale-lock unlock migration.
 * @returns {Promise<boolean>} - Resolves true if migration ran, false if skipped.
 */
export const unlockAllStaleLocks_v2 = async () => {
  // Guard: already done on this device.
  if (localStorage.getItem(MIGRATION_FLAG) === 'complete') {
    return false;
  }

  try {
    console.log('[FT-482] Starting age-based stale operation_data lock migration...');

    const now = Date.now();

    // Fetch all IDB records whose module is 'operations_voyages'.
    const voyageRecords = await db.storage
      .where('module')
      .equals('operations_voyages')
      .toArray();

    if (voyageRecords.length === 0) {
      console.log('[FT-482] No operations_voyages records in IDB — nothing to migrate.');
      localStorage.setItem(MIGRATION_FLAG, 'complete');
      return true;
    }

    let keysChecked = 0;
    let voyagesPatched = 0;
    let locksCleared = 0;
    let locksSkippedYoung = 0;
    let locksSkippedUnparseable = 0;

    for (const record of voyageRecords) {
      keysChecked++;

      const voyages = record.value;
      if (!Array.isArray(voyages)) continue;

      let recordDirty = false;

      const patchedVoyages = voyages.map(voyage => {
        const log = voyage.unifiedLog;
        if (!Array.isArray(log) || log.length === 0) return voyage;

        let voyageDirty = false;
        const patchedLog = log.map(e => {
          if (e && e.type === 'operation_data' && e.locked === true) {
            const entryTs = parseEntryTimestamp(e.id);

            if (entryTs === null) {
              // Cannot determine age — skip to avoid unlocking genuinely in-progress ops.
              locksSkippedUnparseable++;
              console.log(
                `[FT-482] Skipping entry with unparseable ID: voyageId=${voyage.id} id=${e.id}`
              );
              return e;
            }

            const ageMs = now - entryTs;
            if (ageMs <= STALE_THRESHOLD_MS) {
              // Entry is younger than 5 minutes — may still be in progress.
              locksSkippedYoung++;
              return e;
            }

            // Older than 5 minutes — unlock unconditionally.
            voyageDirty = true;
            locksCleared++;
            console.log(
              `[FT-482] Clearing stale lock (age ${Math.round(ageMs / 1000)}s): ` +
              `voyageId=${voyage.id} entryId=${e.id}`
            );
            return { ...e, locked: false };
          }
          return e;
        });

        if (!voyageDirty) return voyage;

        recordDirty = true;
        return { ...voyage, unifiedLog: patchedLog };
      });

      if (recordDirty) {
        await db.storage.put({
          ...record,
          value: patchedVoyages,
          timestamp: Date.now()
        });
        voyagesPatched++;
      }
    }

    // Mark complete — only on success.
    localStorage.setItem(MIGRATION_FLAG, 'complete');

    if (locksCleared > 0) {
      console.log(
        `[FT-482] Migration complete: ${locksCleared} stale lock(s) cleared across ` +
        `${voyagesPatched} IDB record(s) (${keysChecked} checked). ` +
        `Skipped: ${locksSkippedYoung} young, ${locksSkippedUnparseable} unparseable.`
      );
    } else {
      console.log(
        `[FT-482] Migration complete: no stale locks found in ${keysChecked} IDB record(s). ` +
        `Skipped: ${locksSkippedYoung} young, ${locksSkippedUnparseable} unparseable.`
      );
    }

    return true;

  } catch (error) {
    console.error('[FT-482] Age-based stale lock migration failed:', error);
    // Do NOT set the flag — retry on next boot so the issue doesn't persist.
    return false;
  }
};
